import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import StatusBadge from "../../components/StatusBadge";
import RowKebab from "../../components/RowKebab";
import StudentDetailModal from "../../components/StudentDetailModal";
import s from "./StudentsBySectionPanel.module.css";
import StudentProfileForm, {
  uploadStudentPhotoIfPending,
  fetchNextStudentNumber,
  loadStudentPrefix,
  saveStudentPrefix,
  loadStudentPrefixes,
} from "./StudentProfileForm";

function authHeaders() {
  let user = null;
  try { user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { /* ignore */ }
  return {
    "Content-Type": "application/json",
    "X-User-Role":  user?.role     || "",
    "X-Username":   user?.username || "",
    ...(user?.linkedEntityId != null ? { "X-Linked-Entity-Id": String(user.linkedEntityId) } : {}),
    ...(user?.linkedEntityType     ? { "X-Linked-Entity-Type": user.linkedEntityType } : {}),
  };
}

function fullName(st) {
  return [st.lastName, st.firstName].filter(Boolean).join(", ") || "(Unnamed)";
}

/** Sortable column header. Renders a button inside the th so keyboard
 *  + screen-reader users can sort, and exposes aria-sort with the
 *  current direction. */
function SortHeader({ label, sortKey, activeKey, dir, onClick }) {
  const isActive = activeKey === sortKey;
  const ariaSort = !isActive ? "none" : (dir === "desc" ? "descending" : "ascending");
  return (
    <th aria-sort={ariaSort} className={s.thSortable}>
      <button
        type="button"
        className={`${s.sortBtn} ${isActive ? s.sortBtnActive : ""}`}
        data-dir={isActive ? dir : "none"}
        onClick={() => onClick(sortKey)}>
        <span>{label}</span>
        <span className={s.sortArrow} aria-hidden="true" />
      </button>
    </th>
  );
}

/** Empty shape shared by the "+ New student" modal. Sections are NOT set
 *  here — a student is linked to a section only by creating an Enrollment
 *  row (EnrollmentController then updates Student.currentSectionId as a
 *  denormalized cache). */
const BLANK_STUDENT = {
  studentNumber: "", firstName: "", middleName: "", lastName: "",
  birthdate: "", sex: "", contact: "", address: "",
  // New students start PENDING — the Enrollments tab flips this to
  // ENROLLED once a section/term is assigned (handled server-side in
  // EnrollmentController).
  currentSectionId: "", status: "PENDING", photoUrl: "",
};

/**
 * Flat-list view of the student roster with School Year + Semester filters
 * and a search box. Section assignment no longer happens on this panel —
 * it's driven by the Enrollments tab now. The denormalized
 * `Student.currentSectionId` is still shown in the Section column.
 *
 * Props:
 *   students       Array<{id, studentNumber, firstName, lastName, middleName,
 *                          currentSectionId, status}>
 *   sections       Array<{id, name, gradeLevelId, schoolYearId}>
 *   gradeLevels    Array<{id, name, ordering}>
 *   schoolYears    Array<{id, label, active}>
 *   semesters      Array<{id, label, schoolYearId, active}>
 *   enrollments    Array<{id, studentId, schoolYearId, semesterId, sectionId, status}>
 *   onEdit         fn(studentRow)
 *   onDelete       fn(studentRow)
 *   onAfterCreate  fn()  — parent refreshes student list
 */
export default function StudentsBySectionPanel({
  students, sections, gradeLevels,
  schoolYears = [], semesters = [], enrollments = [],
  loading = false,
  onEdit, onDelete, onAfterCreate,
}) {
  const [syId,     setSyId]     = useState("");
  const [semId,    setSemId]    = useState("");
  const [search,   setSearch]   = useState("");
  // Sort: null key = default (name asc, the historic behavior); otherwise
  // one of "studentNumber" | "name" | "section" | "status". Tri-state click
  // cycles asc → desc → cleared, see toggleSort() below.
  const [sortKey,  setSortKey]  = useState(null);
  const [sortDir,  setSortDir]  = useState("asc");

  function toggleSort(key) {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc")  { setSortDir("desc"); return; }
    // Third click resets to default ordering.
    setSortKey(null); setSortDir("asc");
  }

  // Standalone "+ New student" modal state.
  const [newOpen, setNewOpen]       = useState(false);
  const [addForm, setAddForm]       = useState(BLANK_STUDENT);
  const [addPhotoFile, setAddPhoto] = useState(null);
  const [addBusy, setAddBusy]       = useState(false);
  const [addError, setAddError]     = useState("");

  // Read-only student profile (with Documents). Opens via the row name
  // click or the kebab menu — separate from the Edit form.
  const [profileTarget, setProfileTarget] = useState(null);
  // Student-number prefix — lazily initialised from localStorage (falling
  // back to the active SY) so it survives across sessions once the admin
  // has customised it. The list drives the dropdown; `prefix` is the
  // currently-selected one.
  const [prefixes, setPrefixes]     = useState(() => loadStudentPrefixes(schoolYears));
  const [prefix, setPrefix]         = useState(() => loadStudentPrefix(schoolYears));

  // Refresh the prefix list when schoolYears hydrates so a cold install's
  // SY-derived default stays in sync.
  useEffect(() => {
    setPrefixes(loadStudentPrefixes(schoolYears));
  }, [schoolYears]);

  // Debounced re-fetch of the next student number whenever the prefix
  // changes while the modal is open. 400 ms feels quick without hammering
  // the endpoint as the admin types.
  useEffect(() => {
    if (!newOpen) return;
    const t = setTimeout(async () => {
      const next = await fetchNextStudentNumber(API_BASE, prefix);
      if (next) setAddForm(prev => ({ ...prev, studentNumber: next }));
    }, 400);
    return () => clearTimeout(t);
  }, [prefix, newOpen]);

  // Escape closes the "+ New student" modal (outside-click no longer does,
  // to avoid losing in-progress form data).
  useEffect(() => {
    if (!newOpen) return;
    function onKey(e) { if (e.key === "Escape") setNewOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [newOpen]);

  function handlePrefixChange(p) {
    setPrefix(p);
    saveStudentPrefix(p);
  }

  // Keep Semester consistent with SY: if the current semester doesn't belong
  // to the selected SY, clear it.
  useEffect(() => {
    if (!semId) return;
    const sem = (semesters || []).find(x => String(x.id) === String(semId));
    if (!sem) return;
    if (syId && String(sem.schoolYearId) !== String(syId)) setSemId("");
  }, [syId, semesters, semId]);

  const sectionsById    = useMemo(() => Object.fromEntries((sections || []).map(r => [r.id, r])), [sections]);
  const gradeLevelsById = useMemo(() => Object.fromEntries((gradeLevels || []).map(g => [g.id, g])), [gradeLevels]);

  /** studentId → Set<schoolYearId> / Set<semesterId> derived from enrollments.
   *  Used so the SY + Semester filter on this tab covers students who've had
   *  any enrollment in that term (not just whoever is currently cached in
   *  Student.currentSectionId for that SY). */
  const enrollmentsByStudent = useMemo(() => {
    const map = new Map();
    for (const e of (enrollments || [])) {
      if (!map.has(e.studentId)) map.set(e.studentId, { sy: new Set(), sem: new Set() });
      const entry = map.get(e.studentId);
      if (e.schoolYearId != null) entry.sy.add(String(e.schoolYearId));
      if (e.semesterId   != null) entry.sem.add(String(e.semesterId));
    }
    return map;
  }, [enrollments]);

  const flatStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = (students || [])
      .map(st => {
        const sec = st.currentSectionId ? sectionsById[st.currentSectionId] : null;
        const grade = sec ? gradeLevelsById[sec.gradeLevelId] : null;
        const sectionLabel = sec
          ? (grade ? `${grade.name} · ${sec.name}` : sec.name)
          : "";
        return { st, sec, sectionLabel };
      })
      .filter(r => {
        // SY filter: student has an enrollment in this SY, OR their current
        // section is in this SY (so students enrolled pre-enrollments data
        // aren't invisible).
        if (syId) {
          const syMatchViaEnroll = enrollmentsByStudent.get(r.st.id)?.sy.has(String(syId));
          const syMatchViaSection = r.sec && String(r.sec.schoolYearId) === String(syId);
          if (!syMatchViaEnroll && !syMatchViaSection) return false;
        }
        // Semester filter: stricter — only via enrollments.
        if (semId) {
          if (!enrollmentsByStudent.get(r.st.id)?.sem.has(String(semId))) return false;
        }
        if (!q) return true;
        const hay = [
          r.st.studentNumber,
          fullName(r.st),
          r.sectionLabel,
          r.st.status,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });

    // Sort key chooser. Default (sortKey=null) preserves the historic
    // alphabetical-by-name behaviour. localeCompare handles case + accents
    // consistently; numeric:true gives natural ordering for student #.
    const cmp = (() => {
      if (sortKey === null) {
        return (a, b) => fullName(a.st).localeCompare(fullName(b.st));
      }
      const dir = sortDir === "desc" ? -1 : 1;
      const lc  = (x, y) => x.localeCompare(y, undefined, { numeric: true, sensitivity: "base" });
      switch (sortKey) {
        case "studentNumber":
          return (a, b) => dir * lc(a.st.studentNumber || "", b.st.studentNumber || "");
        case "name":
          return (a, b) => dir * lc(fullName(a.st), fullName(b.st));
        case "section":
          // Empty sections always sort to the bottom regardless of dir,
          // so an asc/desc flip never buries real sections under blanks.
          return (a, b) => {
            const ax = a.sectionLabel || "";
            const bx = b.sectionLabel || "";
            if (!ax && bx) return 1;
            if (ax && !bx) return -1;
            return dir * lc(ax, bx);
          };
        case "status":
          return (a, b) => dir * lc(a.st.status || "", b.st.status || "");
        default:
          return (a, b) => fullName(a.st).localeCompare(fullName(b.st));
      }
    })();
    return filtered.sort(cmp);
  }, [students, search, syId, semId, sectionsById, gradeLevelsById, enrollmentsByStudent, sortKey, sortDir]);

  async function saveNewStudent() {
    const f = addForm;
    if (!f.studentNumber?.trim() || !f.firstName?.trim() || !f.lastName?.trim()) {
      setAddError("Student #, first name, and last name are required.");
      return;
    }
    setAddBusy(true); setAddError("");
    try {
      // Section is intentionally NOT set here — the Enrollment tab is the
      // single entry point for section assignment. currentSectionId stays
      // null until an enrollment row is created.
      const payload = {
        studentNumber: f.studentNumber.trim(),
        firstName:     f.firstName.trim(),
        lastName:      f.lastName.trim(),
        middleName:    f.middleName?.trim() || null,
        birthdate:     f.birthdate || null,
        sex:           f.sex || null,
        address:       f.address?.trim() || null,
        contact:       f.contact?.trim() || null,
        // New students are PENDING until they get an enrollment row —
        // EnrollmentController.create flips them to ENROLLED server-side.
        status:        "PENDING",
      };
      const res = await fetch(`${API_BASE}/api/students`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      const created = await res.json();

      if (addPhotoFile) {
        try { await uploadStudentPhotoIfPending(API_BASE, authHeaders, created.id, addPhotoFile); }
        catch (pe) { setAddError(`Student created, but photo upload failed: ${pe.message}`); }
      }

      setNewOpen(false);
      setAddForm(BLANK_STUDENT);
      setAddPhoto(null);
      onAfterCreate && onAfterCreate();
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAddBusy(false);
    }
  }

  const filteredSemesters = syId
    ? (semesters || []).filter(x => String(x.schoolYearId) === String(syId))
    : (semesters || []);

  return (
    <>
      {/* Filter bar — SY, Semester, Search, + New */}
      <div className={s.filterBar}>
        <label className={s.filterField}>
          <span>School Year</span>
          <select value={syId} onChange={e => setSyId(e.target.value)}>
            <option value="">All years</option>
            {(schoolYears || []).map(y => (
              <option key={y.id} value={y.id}>
                {y.label}{y.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className={s.filterField}>
          <span>Semester</span>
          <select value={semId} onChange={e => setSemId(e.target.value)} disabled={!syId && filteredSemesters.length === 0}>
            <option value="">All semesters</option>
            {filteredSemesters.map(x => (
              <option key={x.id} value={x.id}>
                {x.label}{x.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className={`${s.filterField} ${s.searchField}`}>
          <span>Search</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Student #, name, section, or status…"
          />
        </label>

        <button
          type="button"
          className={s.btnPrimary}
          onClick={() => {
            // Reset form state; the useEffect on `prefix + newOpen` fetches
            // the next number once the modal is mounted.
            setAddError("");
            setAddForm(BLANK_STUDENT);
            setAddPhoto(null);
            setNewOpen(true);
          }}>
          + New student
        </button>
      </div>

      <div className={s.summary}>
        <span className={s.pill}>
          <span className={s.pillCount}>{flatStudents.length}</span> of {(students || []).length} student{(students || []).length === 1 ? "" : "s"}
        </span>
      </div>

      {loading ? (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <SortHeader label="Student #" sortKey="studentNumber" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Name"      sortKey="name"          activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Section"   sortKey="section"       activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Status"    sortKey="status"        activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <th className={s.actionsCol}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`} className={s.skelRow}>
                  <td><span className={`${s.skel} ${s.skelPill}`} /></td>
                  <td><span className={`${s.skel} ${s.skelText}`} /></td>
                  <td><span className={`${s.skel} ${s.skelText}`} /></td>
                  <td><span className={`${s.skel} ${s.skelStatus}`} /></td>
                  <td className={s.actionsCol}><span className={`${s.skel} ${s.skelActions}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : flatStudents.length === 0 ? (
        <div className={s.empty}>
          {(students || []).length === 0
            ? "No students yet."
            : "No students match the current filters."}
        </div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <SortHeader label="Student #" sortKey="studentNumber" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Name"      sortKey="name"          activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Section"   sortKey="section"       activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Status"    sortKey="status"        activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <th className={s.actionsCol}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flatStudents.map(({ st, sectionLabel }) => (
                <tr key={st.id}>
                  <td><code className={s.numberPill}>{st.studentNumber || "—"}</code></td>
                  <td>
                    {/* Name acts as the entry point to the full profile +
                        documents modal. Styled as a button-link so it's
                        visibly affordant without disrupting the row layout. */}
                    <button
                      type="button"
                      className={s.nameLink}
                      onClick={() => setProfileTarget(st)}
                      title="View profile">
                      {fullName(st)}
                    </button>
                  </td>
                  <td>{sectionLabel || <span className={s.warnInline}>no section</span>}</td>
                  <td><StatusBadge status={st.status} /></td>
                  <td className={s.actionsCol}>
                    <div className={s.rowActions}>
                      <button type="button" className={s.btnEdit} onClick={() => onEdit(st)}>Edit</button>
                      <RowKebab
                        items={[
                          { label: "View profile", onClick: () => setProfileTarget(st) },
                          { label: "Delete", danger: true, onClick: () => onDelete(st) },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Read-only profile modal (with admin-gated Documents section).
          Self-portals to <body>; we just mount it here unconditionally and
          let it render nothing when no student is selected. */}
      <StudentDetailModal
        open={!!profileTarget}
        student={profileTarget}
        sections={sections}
        gradeLevels={gradeLevels}
        schoolYears={schoolYears}
        semesters={semesters}
        enrollments={enrollments}
        onClose={() => setProfileTarget(null)}
      />

      {/* "+ New student" modal — section is intentionally omitted; the
          Enrollments tab handles section assignment.
          Portaled to <body> so position:fixed centers on the viewport
          instead of on the .shell ancestor (which has a transform that
          creates a fixed-positioning containing block). */}
      {newOpen && createPortal(
        <div className={s.backdrop}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <div className={s.modalHead}>
              <div>
                <h3 className={s.modalTitle}>New student</h3>
                <p className={s.modalSub}>
                  Assign a section later via the Enrollments tab.
                </p>
              </div>
              <button type="button" className={s.closeBtn} onClick={() => setNewOpen(false)}>Close</button>
            </div>

            <div className={s.modalBody}>
              <form className={s.addForm} onSubmit={e => { e.preventDefault(); saveNewStudent(); }}>
                {addError && <div className={s.addError}>{addError}</div>}
                <StudentProfileForm
                  value={addForm}
                  onChange={setAddForm}
                  sections={sections}
                  gradeLevels={gradeLevels}
                  photoFile={addPhotoFile}
                  onPhotoFileChange={setAddPhoto}
                  showSectionPicker={false}
                  disabled={addBusy}
                  prefix={prefix}
                  onPrefixChange={handlePrefixChange}
                  prefixes={prefixes}
                />
              </form>
            </div>

            <div className={s.modalFoot}>
              <button type="button" className={s.btnGhost} onClick={() => setNewOpen(false)} disabled={addBusy}>
                Cancel
              </button>
              <button
                type="button"
                className={s.btnPrimary}
                disabled={addBusy}
                onClick={saveNewStudent}>
                {addBusy ? "Saving…" : "Create student"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
